<!--
  ImportWizardStep2 — ขั้นที่ 2 "ตรวจเนื้อหา (Edit Markdown)" ของ Import Wizard (S12-P1 · Task 5-A)

  ── สัญญากับ ImportWizard.vue (ห้ามเปลี่ยนเอง) ───────────────────────────────
  props
    state      Object  (required)  อ็อบเจ็กต์สถานะกลางที่ views/GraphEditor.vue เป็นเจ้าของ (มติ D10)
                                   → อ่านอย่างเดียว ห้าม mutate ห้าม reassign
    graphKey   String  (required)  คีย์กราฟปัจจุบัน
    canEdit    Boolean (false)     สิทธิ์แก้ไข — ทุก control ต้อง :disabled="!canEdit" + tooltip graphRagReadOnly
    mobile     Boolean (false)     = $vuetify.display.smAndDown ที่ parent คำนวณมาให้แล้ว

  emits
    patch   (fields: Object)   ทางเดียวที่เปลี่ยน state ได้ — parent ทำ Object.assign(wiz, fields)
    notify  ({ text, colour }) เด้ง snackbar ผ่าน GraphEditor (ห้ามใส่ v-snackbar ในไฟล์นี้)
    goto    (step: Number)     ขอย้ายขั้นแบบปุ่มลัดเท่านั้น — ปุ่ม Back/Next ปกติ parent เป็นคน render

  ฟิลด์ของ state ที่ขั้นนี้ "เขียนได้" (ผ่าน patch)
    step2Selected (Array<string> = file key) · step2Prompt (String) · step2ViewMode ('preview'|'raw'|'nodes')
    step2Busy (Boolean) · step2Blocked (Boolean) · step2BlockReason (String ที่ $t แล้ว)

  🔴 ทำไมตัว markdown ถึงไม่ได้เก็บใน state
     เนื้อหาของแต่ละไฟล์อยู่ "ฝั่งเซิร์ฟเวอร์" เป็นเวอร์ชันของ refine session อยู่แล้ว
     (Refine / SaveManualVersion / SelectVersion คืน ResponseRefineSession กลับมาทุกครั้ง)
     และขั้น 5 ส่งแค่ sessionIds ให้ StartExtract → backend หยิบเวอร์ชันที่ถูกเลือกจาก DB เอง
     ⇒ ขั้นนี้จึงเก็บแค่ "สำเนาสำหรับแก้" ไว้ใน data().docs ของตัวเอง ไม่ต้องเขียนทับ state.files

  testid ของขั้นนี้ต้องขึ้นต้น wiz- เท่านั้น (wiz-edit-*) — ห้ามใช้ wizard- และห้ามใช้ index ของ array
-->
<template>
  <div data-testid="wiz-step2">
    <!-- ขั้นนี้ถูกข้ามอัตโนมัติเมื่อไม่มีไฟล์ markdown ที่แก้ได้ — กันพลาดถ้าหลุดเข้ามาได้ -->
    <v-alert v-if="!editableFiles.length" type="info" color="#C32E0E" variant="tonal" density="compact" data-testid="wiz-step2-empty">
      {{ $t('graphRagWizStep2Skipped') }}
    </v-alert>

    <template v-else>
      <v-alert v-if="failedCount" type="error" variant="tonal" density="compact" class="mb-3" role="alert" data-testid="wiz-step2-error">
        {{ $t('graphRagWizRefineFailed', { count: failedCount }) }}
      </v-alert>

      <v-row no-gutters class="wiz2-row">
        <!-- ── แผงซ้าย: รายการไฟล์ + prompt รวม ─────────────────────── -->
        <v-col v-if="!mobile || pane === 'list'" cols="12" md="4" class="wiz2-col-left">
          <div class="wiz-section mb-2">{{ $t('graphRagWizEditFiles') }}</div>

          <v-list density="compact" class="wiz2-file-list" data-testid="wiz-edit-file-list">
            <v-list-item
              v-for="f in doneFiles"
              :key="f.key"
              :active="f.key === activeKey"
              :disabled="isTabular(f)"
              :data-testid="'wiz-edit-file-' + f.key"
              @click="!isTabular(f) && openFile(f.key)"
            >
              <template #prepend>
                <v-tooltip v-if="isTabular(f)" :text="$t('graphRagWizTableNotEditable')">
                  <template #activator="{ props }">
                    <span v-bind="props">
                      <v-checkbox-btn :model-value="false" disabled :data-testid="'wiz-edit-file-check-' + f.key" />
                    </span>
                  </template>
                </v-tooltip>
                <v-checkbox-btn
                  v-else
                  color="#C32E0E"
                  :model-value="isSelected(f.key)"
                  :disabled="!canEdit || busy"
                  :aria-label="f.name"
                  :data-testid="'wiz-edit-file-check-' + f.key"
                  @click.stop
                  @update:model-value="toggleSelected(f.key, $event)"
                />
              </template>

              <v-list-item-title class="wiz-file-name">{{ f.name }}</v-list-item-title>
              <v-list-item-subtitle v-if="isTabular(f)" class="wiz-sub">{{ $t('graphRagWizTableNotEditable') }}</v-list-item-subtitle>
              <v-list-item-subtitle v-else class="wiz-sub">{{ $t('graphRagWizEditVersions', { count: versionCount(f.key) }) }}</v-list-item-subtitle>

              <template #append>
                <v-icon v-if="!isTabular(f) && hasWarning(f.key)" size="small" color="warning">mdi-alert-circle-outline</v-icon>
                <v-icon v-else-if="!isTabular(f)" size="small" color="success">mdi-check-circle</v-icon>
                <v-icon v-if="mobile && !isTabular(f)" size="small" class="ml-1">mdi-chevron-right</v-icon>
              </template>
            </v-list-item>
          </v-list>

          <div class="wiz-sub mt-1">{{ $t('graphRagWizEditSelectedCount', { count: selectedKeys.length }) }}</div>

          <!-- prompt รวมของไฟล์ที่ติ๊ก — ไม่บังคับ (เว้นว่าง = ให้ AI จัดตาม prompt ระบบอย่างเดียว)
               คำอธิบายอยู่นอกช่อง เพราะ label ของ v-textarea ถูกตัดเหลือบรรทัดเดียวในคอลัมน์แคบ -->
          <label for="wiz-edit-shared-prompt-input" class="wiz-sub d-block mt-3 mb-1">
            {{ $t('graphRagWizSharedPrompt') }} ({{ $t('graphRagWizOptional') }})
          </label>
          <v-textarea
            id="wiz-edit-shared-prompt-input"
            :model-value="state.step2Prompt"
            :disabled="!canEdit || busy"
            data-testid="wiz-edit-shared-prompt"
            rows="3"
            density="compact"
            variant="outlined"
            hide-details
            @update:model-value="patch({ step2Prompt: $event })"
          />

          <!-- ระหว่างทำงานปุ่มเป็น :loading อยู่แล้ว ไม่ต้องขึ้นเหตุผลซ้ำ (จะกลายเป็นเหตุผลผิด) -->
          <v-tooltip :text="refineSelectedTooltip" :disabled="!refineSelectedDisabled || busy">
            <template #activator="{ props }">
              <span v-bind="props" class="d-inline-block mt-3">
                <v-btn
                  color="#C32E0E"
                  data-testid="wiz-edit-refine-selected"
                  :loading="busy"
                  :disabled="refineSelectedDisabled"
                  @click="!refineSelectedDisabled && refineSelected()"
                >
                  {{ $t('graphRagWizRefineSelected', { count: selectedKeys.length }) }}
                </v-btn>
              </span>
            </template>
          </v-tooltip>
        </v-col>

        <!-- ── แผงขวา: ตัวแก้ไฟล์ที่เลือกอยู่ ─────────────────────────── -->
        <v-col v-if="!mobile || pane === 'editor'" cols="12" md="8" class="wiz2-col-right">
          <v-btn v-if="mobile" variant="text" size="small" prepend-icon="mdi-chevron-left" class="mb-2" data-testid="wiz-edit-back-to-list" @click="pane = 'list'">
            {{ $t('graphRagWizEditBackToList') }}
          </v-btn>

          <div class="d-flex align-center flex-wrap mb-2" style="gap: 8px">
            <div class="wiz-section">{{ activeFile ? activeFile.name : '' }}</div>
            <v-spacer />
            <!-- บันทึกข้อความที่แก้เองในแท็บแก้ข้อความ/ดูเป็นโหนดเป็นเวอร์ชันใหม่ — ทางเดียวที่การแก้เองไปถึงขั้นสร้างกราฟ
                 (ขั้น 5 หยิบเวอร์ชันที่เลือกจากเซิร์ฟเวอร์ ไม่ใช่ข้อความบนจอ) · กดได้เมื่อมีการแก้ที่ยังไม่ได้บันทึก -->
            <v-tooltip :text="saveVersionTooltip" :disabled="!saveVersionDisabled || busy || doc.saving || doc.loading">
              <template #activator="{ props }">
                <span v-bind="props">
                  <v-btn color="#C32E0E" size="small" data-testid="wiz-edit-save-version" :loading="doc.saving" :disabled="saveVersionDisabled" @click="!saveVersionDisabled && saveVersion()">
                    {{ $t('graphRagWizSaveVersion') }}
                  </v-btn>
                </span>
              </template>
            </v-tooltip>
            <v-tooltip :text="$t('graphRagReadOnly')" :disabled="canEdit">
              <template #activator="{ props }">
                <span v-bind="props">
                  <v-btn variant="outlined" size="small" data-testid="wiz-edit-reset" :loading="doc.resetting" :disabled="!canEdit || busy" @click="canEdit && !busy && resetFile()">
                    {{ $t('graphRagWizResetFile') }}
                  </v-btn>
                </span>
              </template>
            </v-tooltip>
          </div>

          <v-btn-toggle
            :model-value="state.step2ViewMode"
            mandatory
            density="compact"
            color="#C32E0E"
            class="mb-2"
            data-testid="wiz-edit-view-mode"
            @update:model-value="patch({ step2ViewMode: $event })"
          >
            <v-btn value="preview" size="small" data-testid="wiz-edit-preview">{{ $t('graphRagWizViewPreview') }}</v-btn>
            <v-btn value="raw" size="small" data-testid="wiz-edit-raw">{{ $t('graphRagWizViewRaw') }}</v-btn>
            <v-btn value="nodes" size="small" data-testid="wiz-edit-nodes">{{ $t('graphRagWizViewNodes') }}</v-btn>
          </v-btn-toggle>

          <div class="wiz2-editor">
            <v-overlay :model-value="busy" contained persistent class="align-center justify-center">
              <div class="text-center">
                <v-progress-circular indeterminate color="#C32E0E" size="32" />
                <div class="wiz-sub mt-2">{{ $t('graphRagWizRefining') }}</div>
              </div>
            </v-overlay>

            <v-skeleton-loader v-if="doc.loading" type="paragraph" />

            <template v-else>
              <!-- ดูตัวอย่าง -->
              <pre v-if="state.step2ViewMode === 'preview'" class="markdown-box wiz2-preview">{{ doc.draft }}</pre>

              <!-- แก้ข้อความดิบ -->
              <v-textarea
                v-else-if="state.step2ViewMode === 'raw'"
                class="markdown-box"
                :model-value="doc.draft"
                :aria-label="$t('graphRagWizViewRaw')"
                :readonly="!canEdit"
                rows="16"
                density="compact"
                variant="outlined"
                hide-details
                @update:model-value="onRawInput"
              />

              <!-- ดูเป็นโหนด -->
              <div v-else>
                <div v-if="!nodeBlocks.length" class="wiz-sub">{{ $t('graphRagWizEditNoHeadingNodes') }}</div>
                <div v-for="b in nodeBlocks" :key="b.id" class="wiz2-node mb-2">
                  <div class="wiz-sub mb-1">{{ headingPrefix(b.level) }} {{ b.title }}</div>
                  <v-textarea
                    class="markdown-box"
                    :model-value="b.body"
                    :aria-label="b.title"
                    :readonly="!canEdit"
                    rows="4"
                    density="compact"
                    variant="outlined"
                    hide-details
                    @update:model-value="onNodeInput(b, $event)"
                  />
                </div>
              </div>
            </template>
          </div>

          <!-- error ของไฟล์นี้ -->
          <v-alert v-if="doc.error" type="error" variant="tonal" density="compact" class="mt-2" role="alert">
            {{ doc.error }}
          </v-alert>

          <!-- แถบเตือนรายไฟล์ — เป็น warning ไม่ใช่ error -->
          <v-alert
            v-for="w in activeWarnings"
            :key="w"
            type="warning"
            variant="tonal"
            density="compact"
            class="mt-2"
            aria-live="polite"
            :data-testid="'wiz-edit-warning-' + activeKey"
          >
            {{ w }}
          </v-alert>

          <!-- ไม่มีช่องคำสั่ง/ปุ่มขัดเกลารายไฟล์ — ซ้ำกับแผงซ้าย (ติ๊กเฉพาะไฟล์นั้นแล้วใช้ Refine selected ได้ผลเท่ากัน) -->
        </v-col>
      </v-row>
    </template>
  </div>
</template>

<script>
const CONST_WIZ2_TABULAR_EXT = ['.csv', '.xlsx'];
const CONST_WIZ2_HEADING = /^(#{1,6})\s+(.*)$/;

/** สำเนาสำหรับแก้ของไฟล์หนึ่ง — markdown ตัวจริงอยู่ที่ refine session ฝั่งเซิร์ฟเวอร์ */
function emptyDoc() {
  return { loading: false, saving: false, resetting: false, error: '', markdown: '', draft: '', versions: [], selectedVersionNo: null, dirty: false };
}

export default {
  name: 'ImportWizardStep2',
  props: {
    state: { type: Object, required: true },
    graphKey: { type: String, required: true },
    canEdit: { type: Boolean, default: false },
    mobile: { type: Boolean, default: false },
  },
  emits: ['patch', 'notify', 'goto'],
  data() {
    return {
      docs: {},
      nodeBlocks: [],
      nodePreamble: '',
      activeKey: '',
      pane: 'list',
      failedCount: 0,
      knownKeys: [],
    };
  },
  computed: {
    /** ไฟล์ที่แปลงเสร็จแล้วทั้งหมด — ไฟล์ตารางโชว์ในลิสต์แบบ disabled ตาม §State Matrix */
    doneFiles() {
      return (this.state.files || []).filter((f) => f.status === 'done' && f.session);
    },
    /** ไฟล์ markdown ที่แก้ได้ — กติกาเดียวกับที่ parent ใช้ตัดสินการข้ามขั้นนี้ */
    editableFiles() {
      return this.doneFiles.filter((f) => !this.isTabular(f));
    },
    editableKeys() {
      return this.editableFiles.map((f) => f.key);
    },
    selectedKeys() {
      const keys = this.editableKeys;

      return (this.state.step2Selected || []).filter((k) => keys.includes(k));
    },
    busy() {
      return !!this.state.step2Busy;
    },
    activeFile() {
      return this.editableFiles.find((f) => f.key === this.activeKey) || null;
    },
    /** doc ของไฟล์ที่เลือกอยู่ — คืนค่าว่างแทน null เพื่อให้ template ไม่ต้อง guard ทุกจุด */
    doc() {
      return this.docs[this.activeKey] || emptyDoc();
    },
    activeWarnings() {
      const warnings = [];
      const text = String(this.doc.draft || '').trim();
      if (!text) warnings.push(this.$t('graphRagWizWarnEmpty'));
      else if (!this.hasHeading(text)) warnings.push(this.$t('graphRagWizWarnNoHeadings'));

      return warnings;
    },
    refineSelectedDisabled() {
      return !this.canEdit || this.busy || !this.selectedKeys.length || !this.state.modelId;
    },
    refineSelectedTooltip() {
      if (!this.canEdit) return this.$t('graphRagReadOnly');
      if (!this.state.modelId) return this.$t('graphRagWizNoModel');

      return this.$t('graphRagWizRefineSelectNone');
    },
    /** บันทึกได้เมื่อมีข้อความที่แก้แล้วยังไม่ได้บันทึก — กดตอนไม่ได้แก้ backend ก็ไม่สร้างเวอร์ชันซ้ำอยู่แล้ว แต่ปุ่มที่กดได้ทำให้เข้าใจผิด */
    saveVersionDisabled() {
      return !this.canEdit || this.busy || !this.activeKey || this.doc.loading || this.doc.saving || !this.doc.dirty;
    },
    saveVersionTooltip() {
      if (!this.canEdit) return this.$t('graphRagReadOnly');

      return this.$t('graphRagNoChange');
    },
  },
  watch: {
    editableKeys: {
      immediate: true,
      handler(keys) {
        this.syncSelection(keys);
        if (!keys.includes(this.activeKey)) this.setActive(keys.length ? keys[0] : '');
        // โหลดเวอร์ชันของทุกไฟล์ไว้ล่วงหน้า เพื่อให้จำนวนเวอร์ชันกับไอคอนเตือนในลิสต์ตรงความจริงตั้งแต่แรก
        this.editableFiles.forEach((f) => this.ensureDoc(f));
      },
    },
    'state.step2ViewMode': function onViewMode() {
      this.syncNodeBlocks();
    },
  },
  methods: {
    /** ทางเดียวที่ขั้นนี้เปลี่ยน state กลางได้ */
    patch(fields) {
      this.$emit('patch', fields);
    },
    notify(text, colour) {
      this.$emit('notify', { text, colour: colour || 'success' });
    },
    isTabular(file) {
      return CONST_WIZ2_TABULAR_EXT.includes(file.ext);
    },
    isSelected(key) {
      return this.selectedKeys.includes(key);
    },
    hasHeading(text) {
      return String(text || '')
        .split('\n')
        .some((line) => CONST_WIZ2_HEADING.test(line));
    },
    hasWarning(key) {
      const doc = this.docs[key];
      if (!doc || doc.loading) return false;
      const text = String(doc.draft || '').trim();

      return !text || !this.hasHeading(text);
    },
    versionCount(key) {
      const doc = this.docs[key];

      return doc ? doc.versions.length : 0;
    },
    headingPrefix(level) {
      return '#'.repeat(Math.max(1, Math.min(6, level)));
    },
    /** ไฟล์ที่เพิ่งเข้ามาให้ติ๊กไว้ก่อน · ไฟล์ที่หายไปให้ถอดออก · ที่ผู้ใช้ติ๊กออกเองห้ามติ๊กกลับ */
    syncSelection(keys) {
      const current = this.state.step2Selected || [];
      const fresh = keys.filter((k) => !this.knownKeys.includes(k));
      const kept = current.filter((k) => keys.includes(k));
      this.knownKeys = keys.slice();

      const next = kept.concat(fresh.filter((k) => !kept.includes(k)));
      if (next.length !== current.length || next.some((k, i) => k !== current[i])) this.patch({ step2Selected: next });
    },
    toggleSelected(key, on) {
      if (!this.canEdit || this.busy) return;
      const next = this.selectedKeys.filter((k) => k !== key);
      if (on) next.push(key);

      this.patch({ step2Selected: next });
    },
    openFile(key) {
      this.setActive(key);
      if (this.mobile) this.pane = 'editor';
    },
    setActive(key) {
      this.activeKey = key;
      if (!key) return;

      const file = this.editableFiles.find((f) => f.key === key);
      if (file) this.ensureDoc(file).then(() => this.syncNodeBlocks());
    },
    /** โหลดเวอร์ชันของไฟล์จากเซิร์ฟเวอร์ครั้งแรกที่ต้องใช้ — ระหว่างรอใช้ markdown ที่ได้ตอนแปลงไปก่อน */
    ensureDoc(file) {
      const key = file.key;
      if (this.docs[key]) return Promise.resolve(this.docs[key]);

      // ต้องหยิบตัวที่ผ่าน proxy ของ Vue กลับมาใช้ ไม่อย่างนั้นการแก้ค่าทีหลังจะไม่ทำให้จอเปลี่ยน
      this.docs[key] = emptyDoc();
      const doc = this.docs[key];
      doc.loading = true;
      doc.markdown = file.session.markdown || '';
      doc.draft = doc.markdown;

      return new Promise((resolve) => {
        this.$call.GraphRagGetRefineSession(
          this.graphKey,
          file.session.sessionId,
          (res) => {
            doc.loading = false;
            if (res.status !== 0) doc.error = res.message;
            else this.applySession(key, res.data);

            return resolve(doc);
          },
          () => {
            doc.loading = false;
            doc.error = this.$t('graphRagRequestFailed');

            return resolve(doc);
          }
        );
      });
    },
    applySession(key, session) {
      const doc = this.docs[key];
      if (!doc || !session) return;

      doc.error = '';
      doc.versions = session.versions || [];
      doc.selectedVersionNo = session.selectedVersionNo;
      doc.markdown = session.selectedMarkdown || '';
      doc.draft = doc.markdown;
      doc.dirty = false;
      if (key === this.activeKey) this.syncNodeBlocks();
    },
    onRawInput(value) {
      if (!this.canEdit) return;
      const doc = this.docs[this.activeKey];
      if (!doc) return;

      doc.draft = value;
      doc.dirty = doc.draft !== doc.markdown;
    },
    /** แยก markdown เป็นบล็อกตามหัวข้อ — เก็บไว้ใน data เพื่อไม่ต้อง re-parse ทุกคีย์ที่พิมพ์ */
    syncNodeBlocks() {
      if (this.state.step2ViewMode !== 'nodes') return;

      const doc = this.docs[this.activeKey];
      const blocks = [];
      let preamble = [];
      let current = null;
      let seq = 0;

      String(doc ? doc.draft : '')
        .split('\n')
        .forEach((line) => {
          const match = CONST_WIZ2_HEADING.exec(line);
          if (match) {
            if (current) blocks.push(current);
            seq += 1;
            current = { id: this.activeKey + '-h' + seq, level: match[1].length, title: match[2], body: [] };
          } else if (current) current.body.push(line);
          else preamble.push(line);
        });

      if (current) blocks.push(current);
      this.nodeBlocks = blocks.map((b) => ({ id: b.id, level: b.level, title: b.title, body: b.body.join('\n') }));
      this.nodePreamble = preamble.join('\n');
    },
    onNodeInput(block, value) {
      if (!this.canEdit) return;
      const doc = this.docs[this.activeKey];
      if (!doc) return;

      block.body = value;
      const lines = [];
      if (this.nodePreamble) lines.push(this.nodePreamble);
      this.nodeBlocks.forEach((b) => {
        lines.push(this.headingPrefix(b.level) + ' ' + b.title);
        if (b.body !== '') lines.push(b.body);
      });

      doc.draft = lines.join('\n');
      doc.dirty = doc.draft !== doc.markdown;
    },
    /** ขัดเกลาไฟล์เดียว — คืน true เมื่อสำเร็จ เพื่อให้คิวนับไฟล์ที่ล้มได้ */
    refineOne(file, instruction) {
      const key = file.key;

      return this.ensureDoc(file).then(
        () =>
          new Promise((resolve) => {
            const doc = this.docs[key];
            doc.error = '';

            this.$call.GraphRagRefineMarkdown(
              { graphKey: this.graphKey, sessionId: file.session.sessionId, modelId: this.state.modelId, instruction: instruction },
              (res) => {
                if (res.status !== 0) {
                  doc.error = res.message;

                  return resolve(false);
                }

                this.applySession(key, res.data);

                return resolve(true);
              },
              () => {
                doc.error = this.$t('graphRagRequestFailed');

                return resolve(false);
              }
            );
          })
      );
    },
    /** ขัดเกลาหลายไฟล์ — ต่อคิวทีละไฟล์ ไม่ยิงพร้อมกัน · ไฟล์ที่ไม่ได้เลือกไม่ถูกแตะเลย */
    async refineSelected() {
      if (this.refineSelectedDisabled) return;

      const files = this.editableFiles.filter((f) => this.selectedKeys.includes(f.key));
      const instruction = String(this.state.step2Prompt || '').trim();
      this.failedCount = 0;
      this.patch({ step2Busy: true });

      // ต้องเรียงคิว ไม่ยิงพร้อมกัน — LLM หลายคำขอพร้อมกันทำให้ backend ล้มทั้งชุด
      let failed = 0;
      for (const file of files) {
        const ok = await this.refineOne(file, instruction);
        if (!ok) failed += 1;
      }

      this.failedCount = failed;
      this.patch({ step2Busy: false });

      if (failed) this.notify(this.$t('graphRagWizRefineFailed', { count: failed }), 'error');
      else this.notify(this.$t('graphRagSaved'), 'success');
    },
    saveVersion() {
      const file = this.activeFile;
      const doc = this.docs[this.activeKey];
      if (!file || !doc || !this.canEdit) return;

      doc.saving = true;
      doc.error = '';

      this.$call.GraphRagSaveManualVersion(
        { graphKey: this.graphKey, sessionId: file.session.sessionId, markdown: doc.draft },
        (res) => {
          doc.saving = false;
          if (res.status !== 0) {
            doc.error = res.message;

            return this.notify(res.message, 'error');
          }

          this.applySession(file.key, res.data);

          return this.notify(this.$t('graphRagSaved'), 'success');
        },
        () => {
          doc.saving = false;
          doc.error = this.$t('graphRagRequestFailed');
          this.notify(this.$t('graphRagRequestFailed'), 'error');
        }
      );
    },
    /** ย้อนกลับเป็นฉบับแรก = เลือกเวอร์ชันที่เลขน้อยที่สุดที่เซิร์ฟเวอร์ส่งมา (ห้าม hardcode 1) */
    resetFile() {
      const file = this.activeFile;
      const doc = this.docs[this.activeKey];
      if (!file || !doc || !this.canEdit) return;

      const first = doc.versions.reduce((min, v) => (min === null || v.versionNo < min ? v.versionNo : min), null);
      if (first === null) return;

      doc.resetting = true;
      doc.error = '';

      this.$call.GraphRagSelectVersion(
        { graphKey: this.graphKey, sessionId: file.session.sessionId, versionNo: first },
        (res) => {
          doc.resetting = false;
          if (res.status !== 0) {
            doc.error = res.message;

            return this.notify(res.message, 'error');
          }

          this.applySession(file.key, res.data);

          return this.notify(this.$t('graphRagSaved'), 'success');
        },
        () => {
          doc.resetting = false;
          doc.error = this.$t('graphRagRequestFailed');
          this.notify(this.$t('graphRagRequestFailed'), 'error');
        }
      );
    },
  },
  mounted() {
    if (this.state.step2Blocked) this.patch({ step2Blocked: false, step2BlockReason: '' });
  },
};
</script>

<style scoped>
.wiz2-row {
  gap: 0;
}
.wiz2-col-left {
  padding-right: 12px;
}
.wiz2-file-list {
  max-height: 320px;
  overflow-y: auto;
}
.wiz2-editor {
  position: relative;
  min-height: 220px;
}
.wiz2-preview {
  max-height: 420px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  padding: 8px 12px;
  margin: 0;
}
.wiz2-node {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 5px;
  padding: 8px;
}
.wiz-section {
  font-size: 15px;
}
.wiz-sub {
  font-size: 12px;
  color: #646464;
}
.wiz-file-name {
  font-size: 13px;
}
.markdown-box,
.markdown-box :deep(textarea) {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
  line-height: 1.6;
}
</style>
